'use client';

import { ICartItem } from '@/api/types/cart';
import AnniCountDown from '@/components/AnniCountDown/AnniCountDown';
import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import Tooltip from '@/components/Tooltip';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { InfoFilled } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useContext, useMemo, useState } from 'react';

interface ProductImageTagsProps {
  item: ICartItem;
  itemType: number;
  selected: boolean;
  itemPromotion?: any;
  outStock: boolean;
  flag1: boolean;
  flag2: boolean;
  isPc?: boolean; // 是否为PC端样式
  overlayMode?: boolean; // 是否为覆盖模式（PC端覆盖在图片上的标签）
  // 倒计时相关属性
  giftCountDown?: number; // 赠品倒计时时间
  giftAllowType?: number; // 赠品是否可结算 1可结算 giftCountDown为可计算结束时间倒计时  2不可结算 giftCountDown为可计算开始时间倒计时
  itemGiftInfo?: any; // 独立赠品的intro提示
}

export function ProductImageTags({
  item,
  itemType,
  selected,
  itemPromotion,
  outStock,
  flag1,
  flag2,
  isPc = false,
  overlayMode = false,
  giftCountDown = 0,
  giftAllowType = 0,
  itemGiftInfo = {},
}: ProductImageTagsProps) {
  const globalT = useTranslations('cart');
  const t = useTranslations('cart.productImageTags');
  const { deviceType } = useContext(ResponsiveContext);
  const publicResourceData = useStore((state) => state.publicResourceData);
  const productConfig = publicResourceData?.info?.product_config;
  const [showPreCountDown, setShowPreCountDown] = useState(true); // 默认显示预售倒计时
  const [afterAnniGreaterFlag, setAfterAnniGreaterFlag] = useState(false);

  // 判断是否为PC端
  const isWebPlatform = deviceType === EPlatform.WEB || isPc;

  // 使用useMemo优化性能，避免重复计算
  const popoverContent = useMemo(() => {
    const contents: string[] = [];

    // 限购说明
    const { product: { limit_buy, limit_rule } = {} } = item;
    if (limit_buy) {
      let str = '';
      if (limit_rule === 2 || limit_rule === 5) {
        str = 'account';
      } else if (limit_rule === 3 || limit_rule === 4) {
        str = 'order';
      }
      if (str) {
        contents.push(
          `${limit_buy} ${limit_buy > 1 ? globalT('limitBuyDialog.cutPriceItemsPlural') : globalT('limitBuyDialog.cutPriceItems')} ${str === 'account' ? globalT('limitBuyDialog.perAccount') : globalT('limitBuyDialog.perOrder')}`,
        );
      }
    }
    // 第二件半价
    const { product: { exclusion: { half_price } = {} } = {} } = item;
    if (half_price === 1) {
      contents.push(t('halfPriceInvalid'));
    }

    // 不退换
    const { products_promotions_type, products_promotions_show, over_limit_rule } =
      itemPromotion || {};
    if (
      Number(products_promotions_type) === 3 &&
      products_promotions_show === 1 &&
      over_limit_rule === 1
    ) {
      contents.push(t('nonReturnableOrExchange'));
    }

    // 仅限镜框优惠
    const { product: { is_glass } = {} } = item;
    if (is_glass === 2) {
      contents.push(t('discountOnlyForFrames'));
    }

    return contents.join('\n');
  }, [item, itemPromotion]);

  // 检查是否禁用Tooltip
  const isTooltipDisabled = useMemo(() => {
    return !popoverContent;
  }, [popoverContent]);

  // 处理倒计时结束
  const handleCountDownEnd = () => {
    setShowPreCountDown(false);
  };

  // 处理afterAnniGreaterFlag变化
  const handleChangeAfterAnniGreaterFlag = (flag: boolean) => {
    setAfterAnniGreaterFlag(flag);
  };

  // 渲染主要内容
  const renderContent = () => {
    // PC端覆盖模式标签（覆盖在商品图片上）
    if (isWebPlatform && overlayMode) {
      return (
        <>
          {/* PC端 - 售罄状态 */}
          {itemType === 1 && outStock && (
            <div
              translate="no"
              className="absolute inset-0 bg-white/50 flex items-center justify-center"
            >
              <div className="flex items-center justify-center w-[80px] h-[80px] rounded-full text-center bg-black/70 text-white text-[14px]">
                {item.stock < 1 ? (
                  <p className="text-center" dangerouslySetInnerHTML={{ __html: t('soldOut') }} />
                ) : (
                  <div className="flex-col text-[12px]">
                    <p
                      className="text-center"
                      dangerouslySetInnerHTML={{ __html: t('noRestockWithLineBreak') }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PC端 - 库存提示 */}
          {productConfig?.stock_show_surplus_qty === 1 &&
            productConfig?.stock_small_prompt_show === 1 &&
            productConfig?.stock_show_cart === 1 &&
            item.stock <= productConfig?.stock_small_prompt_qty &&
            itemType !== 4 &&
            item.stock > 0 && (
              <div
                translate="no"
                className={`absolute text-center text-[#b06814] ${itemType === 4 ? 'hidden' : ''} ${item.product.is_glass === 1 ? 'text-[12px] 2xl:text-[14px] py-[16px] bottom-0 left-0 right-0' : 'text-[10px] 2xl:text-[12px] bg-[#ffffff] w-[85%] py-[5px] rounded-[50px] bottom-[10px]'}`}
              >
                {t('lowInventory', { stock: item.stock })}
              </div>
            )}
        </>
      );
    }

    // PC端上方模式标签（在商品图片上方显示）- 参考LimitBuyPop组件
    if (isWebPlatform && !overlayMode) {
      return (
        <>
          {/* PC端 - 赠品标签和倒计时 */}
          {itemType === 4 && (
            <div className="flex items-center">
              <span className="bg-[#baacff] px-[9px] h-[20px] leading-[20px] rounded-[4px] text-[12px] text-primary-black mr-[8px]">
                {t('gift')}
              </span>
              {/* 预售倒计时 */}
              {giftAllowType === 2 && showPreCountDown && giftCountDown > 0 && (
                <div className="flex justify-start items-center min-h-[20px] box-border">
                  <AnniCountDown
                    key={item.cart_id}
                    time={giftCountDown}
                    preOrAfter="pre"
                    intro={itemGiftInfo?.intro || ''}
                    onCountDownEnd={handleCountDownEnd}
                  />
                </div>
              )}
              {/* 结束倒计时 - 可结算时显示 */}
              {giftAllowType === 1 && (
                <div
                  className={`flex justify-start items-center min-h-[20px] box-border ${afterAnniGreaterFlag ? 'hidden' : ''}`}
                >
                  <AnniCountDown
                    key={item.cart_id}
                    time={giftCountDown}
                    preOrAfter="after"
                    intro={itemGiftInfo?.intro || ''}
                    onChangeAfterAnniGreaterFlag={handleChangeAfterAnniGreaterFlag}
                  />
                </div>
              )}
            </div>
          )}
          {/* 已兑换商品覆盖标签 */}
          {itemType === 5 && (
            <div className="bg-[#ecebfd] text-center h-[23px] w-[126px] leading-[23px] text-[14px] rounded-[4px]">
              <span className="text-sm font-medium">{t('redeemedItem')}</span>
            </div>
          )}

          {/* PC端 - 促销标签 - 参考LimitBuyPop组件结构 */}
          {/* 未勾选状态的促销标签 */}
          {!selected && itemType === 1 && (
            <>
              {/* Flash Sale 标签 */}
              {String(item.product.products_promotions_type) === '2' && (
                <div className="flex items-center w-fit cursor-pointer">
                  <span className="h-[20px] leading-[20px] px-[9px] text-primary-black bg-[#fca6ff] rounded-[4px] mr-[8px] whitespace-nowrap text-[12px]">
                    {t('flashSale')}
                  </span>
                  <span className="block text-[14px] text-primary-black mr-[8px] whitespace-nowrap max-w-[470px] 2xl:max-w-[350px] truncate">
                    {t('limitedStock')}
                  </span>
                  {!isTooltipDisabled && (
                    <Tooltip
                      title={popoverContent}
                      placement="bottom"
                      variant="light"
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: '300px',
                          fontSize: '14px',
                          padding: '8px 12px',
                          whiteSpace: 'pre-line',
                          fontFamily: 'var(--font-bricolage)',
                        },
                      }}
                    >
                      <InfoFilled className="text-[#808080] text-[14px] transition-colors" />
                    </Tooltip>
                  )}
                </div>
              )}

              {/* 促销图标 */}
              {[3].includes(Number(item.product.products_promotions_type)) && (
                <div className="flex items-center w-fit cursor-pointer">
                  <span className="h-[20px] mr-[8px]">
                    <Image
                      src={(item.product as any).activity_style?.product_item_image_pc || ''}
                      alt=""
                      width={80}
                      height={20}
                      className="h-[20px] w-auto"
                    />
                  </span>
                  <span className="block text-[14px] text-primary-black mr-[8px] whitespace-nowrap max-w-[470px] 2xl:max-w-[350px] truncate">
                    {(item.product as any).activity_style?.cart_rules_text_pc ||
                      t('limitedStockShort')}
                  </span>
                  {!isTooltipDisabled && (
                    <Tooltip
                      title={popoverContent}
                      placement="bottom"
                      variant="light"
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: '300px',
                          fontSize: '14px',
                          padding: '8px 12px',
                          whiteSpace: 'pre-line',
                          fontFamily: 'var(--font-bricolage)',
                        },
                      }}
                    >
                      <InfoFilled className="text-[#808080] text-[14px] hover:text-[#666666]" />
                    </Tooltip>
                  )}
                </div>
              )}
            </>
          )}

          {/* 已勾选状态的促销标签 */}
          {selected &&
            itemPromotion &&
            itemPromotion.products_promotions_qty > 0 &&
            itemType === 1 && (
              <>
                {/* Flash Sale 标签 */}
                {(flag1 || flag2) && Number(itemPromotion.products_promotions_type) === 2 && (
                  <div className="flex items-center w-fit cursor-pointer">
                    <span className="h-[20px] leading-[20px] px-[9px] text-primary-black bg-[#fca6ff] rounded-[4px] mr-[8px] whitespace-nowrap text-[12px]">
                      {t('flashSale')}
                    </span>
                    <span className="block text-[14px] text-primary-black mr-[8px] whitespace-nowrap max-w-[470px] 2xl:max-w-[350px] truncate">
                      {t('limitedStock')}
                    </span>
                    {!isTooltipDisabled && (
                      <Tooltip
                        title={popoverContent}
                        placement="bottom"
                        variant="light"
                        sx={{
                          '& .MuiTooltip-tooltip': {
                            maxWidth: '300px',
                            fontSize: '14px',
                            padding: '8px 12px',
                            whiteSpace: 'pre-line',
                            fontFamily: 'var(--font-bricolage)',
                          },
                        }}
                      >
                        <InfoFilled className="text-[#808080] text-[14px]" />
                      </Tooltip>
                    )}
                  </div>
                )}

                {/* 促销图标 */}
                {(flag1 || flag2) &&
                  [3].includes(Number(itemPromotion.products_promotions_type)) && (
                    <div className="flex items-center w-fit cursor-pointer">
                      <span className="h-[20px] mr-[8px]">
                        <Image
                          src={
                            (itemPromotion as any).product_info?.activity_style
                              ?.product_item_image_pc || ''
                          }
                          alt=""
                          width={80}
                          height={20}
                          className="h-[20px] w-auto"
                        />
                      </span>
                      <span className="block text-[14px] text-primary-black mr-[8px] whitespace-nowrap max-w-[470px] 2xl:max-w-[350px] truncate">
                        {(itemPromotion as any).product_info?.activity_style?.cart_rules_text_pc ||
                          t('limitedStockShort')}
                      </span>
                      {!isTooltipDisabled && (
                        <Tooltip
                          title={popoverContent}
                          placement="bottom"
                          variant="light"
                          sx={{
                            '& .MuiTooltip-tooltip': {
                              maxWidth: '300px',
                              fontSize: '14px',
                              padding: '8px 12px',
                              whiteSpace: 'pre-line',
                              fontFamily: 'var(--font-bricolage)',
                            },
                          }}
                        >
                          <InfoFilled className="text-[#808080] text-[14px] hover:text-[#666666] transition-colors" />
                        </Tooltip>
                      )}
                    </div>
                  )}
              </>
            )}
        </>
      );
    }

    // 移动端标签样式（默认overlayMode，所有标签覆盖在图片上）
    return (
      <>
        {/* 移动端 - 产品标签（赠品等） */}
        {itemType === 4 && (
          <div className="absolute top-[-1px] left-[-1px] bg-[#baacff] h-[15px] leading-[15px] px-[7px] text-[10px] rounded-tl-[8px] rounded-br-[8px] z-10">
            {t('gift')}
          </div>
        )}

        {/* 移动端 - 售罄状态 */}
        {itemType === 1 && outStock && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <div className="flex items-center justify-center w-[60px] h-[60px] rounded-full text-center bg-black/70 text-white text-[10px]">
              {item.stock < 1 ? (
                <p className="text-center" dangerouslySetInnerHTML={{ __html: t('soldOut') }} />
              ) : (
                <p
                  className="text-center"
                  dangerouslySetInnerHTML={{ __html: t('noRestockWithLineBreak') }}
                />
              )}
            </div>
          </div>
        )}

        {/* 移动端 - 库存提示 */}
        {productConfig?.stock_show_surplus_qty === 1 &&
          productConfig?.stock_small_prompt_show === 1 &&
          productConfig?.stock_show_cart === 1 &&
          item.stock <= productConfig?.stock_small_prompt_qty &&
          itemType !== 4 &&
          item.stock > 0 && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/34 text-white z-[12] text-[10px] rounded-b-[8px] h-[18px] flex items-center justify-center">
              {t('onlyLeft', { stock: item.stock })}
            </div>
          )}

        {/* 移动端特有的促销标签 */}
        <>
          {/* 未勾选状态的促销标签 //products_promotions_type：参与单品促销形式（0未参与促销 1常规促销 2flash sale 3Seckill 4周年庆秒杀） */}
          {!selected && itemType === 1 && (
            <>
              {/* Limited Stock 提示 */}
              {[3].includes(Number(item.product.products_promotions_type)) &&
                item.stock >= (productConfig?.stock_small_prompt_qty || 0) && (
                  <div className="absolute bottom-0 left-0 right-0 text-center text-[#b06814] z-[12] text-[10px] h-[18px] leading-[18px] rounded-b-[8px]">
                    {(item.product as any).activity_style?.cart_rules_text_m}
                  </div>
                )}

              {String(item.product.products_promotions_type) === '2' &&
                item.stock >= (productConfig?.stock_small_prompt_qty || 0) && (
                  <div className="absolute bottom-0 left-0 right-0 text-center text-[#b06814] z-[12] text-[10px] h-[18px] leading-[18px] rounded-b-[8px]">
                    {t('limitedStockShort')}
                  </div>
                )}

              {/* Flash Sale 标签 */}
              {String(item.product.products_promotions_type) === '2' && (
                <div className="absolute top-[-1px] left-[-1px] px-[6px] bg-[#fca6ff] rounded-[8px_0px_8px_0px] text-primary-black z-[12] text-[10px]">
                  {t('flashSale')}
                </div>
              )}

              {/* 促销图标 */}
              {[3].includes(Number(item.product.products_promotions_type)) && (
                <div className="absolute top-[-1px] left-[-1px] z-[9]">
                  <Image
                    src={(item.product as any).activity_style?.product_item_image_m || ''}
                    alt=""
                    width={60}
                    height={15}
                    className="h-[15px] w-auto"
                  />
                </div>
              )}
            </>
          )}

          {/* 已勾选状态的促销标签 */}
          {selected &&
            itemPromotion &&
            itemPromotion.products_promotions_qty > 0 &&
            itemType === 1 && (
              <>
                {/* Limited Stock 提示 */}
                {[3].includes(Number(itemPromotion.products_promotions_type)) &&
                  item.stock >= (productConfig?.stock_small_prompt_qty || 0) && (
                    <div className="absolute bottom-0 left-0 right-0 text-center text-[#b06814] z-[12] text-[10px] h-[18px] leading-[18px] rounded-b-[8px]">
                      {(itemPromotion as any).product_info?.activity_style?.cart_rules_text_m}
                    </div>
                  )}

                {String(itemPromotion.products_promotions_type) === '2' &&
                  item.stock >= (productConfig?.stock_small_prompt_qty || 0) && (
                    <div className="absolute bottom-0 left-0 right-0 text-center text-[#b06814] z-[12] text-[10px] h-[18px] leading-[18px] rounded-b-[8px]">
                      {t('limitedStockShort')}
                    </div>
                  )}

                {/* Flash Sale 标签 */}
                {(flag1 || flag2) && Number(itemPromotion.products_promotions_type) === 2 && (
                  <div className="absolute top-[-1px] left-[-1px] px-[6px] bg-[#fca6ff] rounded-[8px_0px_8px_0px] text-primary-black z-[12] text-[10px]">
                    {t('flashSale')}
                  </div>
                )}

                {/* 促销图标 */}
                {(flag1 || flag2) &&
                  [3].includes(Number(itemPromotion.products_promotions_type)) && (
                    <div className="absolute top-[-1px] left-[-1px] z-[9]">
                      <Image
                        src={
                          (itemPromotion as any).product_info?.activity_style
                            ?.product_item_image_m || ''
                        }
                        alt=""
                        width={60}
                        height={15}
                        className="h-[15px] w-auto"
                      />
                    </div>
                  )}
              </>
            )}
        </>
      </>
    );
  };

  return (
    <>
      {/* 原有的渲染内容 */}
      {renderContent()}
    </>
  );
}
